import { LogLevel, log } from "../utils/log";
import { getWindowParameter } from "../utils/urlParameters";

/**
 * When rciframeurl parameter is present, this module handles the reality check iframe element creation and
 * relaying operator-bound messages to it.
 * rciframeurl is used with Rg API and Legacy RC API, but not with Theos
 */

const IFRAME_ID = "rg_rc_iframe";

// Legacy API methods relayed to the rc iframe
const OPERATOR_METHODS = ["gameLoaded", "gameLoadedHandler", "gamePausedHandler", "playTimeHandler",
    "handshakeSuccess", "handshakeCompleted", "handshakeFail", "handshakeFailed"];

let rcIframe: HTMLIFrameElement | undefined = undefined;

export const showExternalRealityCheck = (): void =>
{
    if (rcIframe) {
        rcIframe.style.display = "block";
    }
};

export const closeExternalRealityCheck = (): void =>
{
    if (rcIframe) {
        rcIframe.style.display = "none";
    }
};

export const initializeRcIframe = (): void =>
{
    if (document.body === null) {
        window.addEventListener("load", initializeRcIframe);
        return;
    }

    window.removeEventListener("load", initializeRcIframe);

    const rcenable = getWindowParameter("rcenable");

    if (rcenable !== "true") {
        return;
    }

    const rcIframeUrl = getWindowParameter("rciframeurl");

    if (rcIframeUrl === undefined || rcIframeUrl === "" || rcIframeUrl.toLowerCase().includes("javascript:")) {
        return;
    }

    if (!rcIframeUrl.startsWith("https:") && !rcIframeUrl.startsWith("http:")) {
        return;
    }

    let element = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;

    if (!element) {
        log("Creating RC iframe", LogLevel.INIT);

        element = document.createElement("iframe");
        element.id = IFRAME_ID;
        element.style.display = "none";
        element.style.position = "fixed";
        element.style.left = "0";
        element.style.top = "0";
        element.style.width = "100%";
        element.style.height = "100%";
        element.style.zIndex = "1001";
        element.frameBorder = "0";
        element.setAttribute("sandbox", "allow-scripts allow-top-navigation-by-user-activation allow-same-origin allow-popups allow-popups-to-escape-sandbox");

        const wrapper = document.getElementById("rc_iframe_wrapper");

        if (wrapper)
        {
            wrapper.appendChild(element);
        }
        else
        {
            document.body.appendChild(element);
        }
    }

    const rcHistoryUrl = getWindowParameter("rchistoryurl") ?? "";
    const homeurl = getWindowParameter("homeurl") ?? "";

    const separator = rcIframeUrl.includes("?") ? "&" : "?";

    element.src = rcIframeUrl + separator + "rchistoryurl=" + rcHistoryUrl + "&homeurl=" + homeurl;

    rcIframe = element;
};

const isForOperator = (data?: {rgMessage?: string; method?: string}): boolean =>
{
    if (!data) {
        return false;
    }

    // Rg API message
    if (data.rgMessage?.startsWith("gprg_")) {
        return true;
    }

    // Legacy RC API message
    if (data.method !== undefined && OPERATOR_METHODS.indexOf(data.method) >= 0) {
        return true;
    }

    return false;
};

/**
 * Relays the message to the rc iframe if present
 * @param data
 */
export const relayPostMessageToRcIframe = (message?: unknown, targetOrigin?: string): void =>
{
    if (rcIframe === undefined || !rcIframe.contentWindow) {
        return;
    }

    if (message === undefined) {
        return;
    }

    const messageData = message as {rgMessage?: string; method?: string};

    if (!isForOperator(messageData)) {
        return;
    }

    rcIframe.contentWindow.postMessage(messageData, targetOrigin ?? "*");
};
